(() => {
  'use strict';

  function initialiseProduct(product) {
    if (product.dataset.rvReady === 'true') return;

    const gallery = product.querySelector('[data-rv-gallery]');
    const mediaItems = gallery ? [...gallery.querySelectorAll('[data-rv-product-media]')] : [];
    const galleryStatus = gallery?.querySelector('[data-rv-gallery-status]');
    const thumbnailList = product.querySelector('[data-rv-gallery-thumbnails]');
    const thumbnails = thumbnailList
      ? [...thumbnailList.querySelectorAll('[data-rv-gallery-thumbnail]')]
      : [];
    const galleryDialog = product.querySelector('[data-rv-gallery-dialog]');
    const lightboxMedia = galleryDialog?.querySelector('[data-rv-lightbox-media]');
    const lightboxStatus = galleryDialog?.querySelector('[data-rv-lightbox-status]');
    const lightboxPrevious = galleryDialog?.querySelector('[data-rv-gallery-previous]');
    const lightboxNext = galleryDialog?.querySelector('[data-rv-gallery-next]');
    const lightboxClose = galleryDialog?.querySelector('[data-rv-gallery-close]');
    let activeMediaIndex = Math.max(
      0,
      mediaItems.findIndex((media) => media.getAttribute('aria-current') === 'true')
    );
    let galleryScrollFrame = 0;

    const scrollItemWithin = (container, item) => {
      if (!container || !item) return;
      const containerBox = container.getBoundingClientRect();
      const itemBox = item.getBoundingClientRect();
      let left = 0;
      let top = 0;

      if (itemBox.left < containerBox.left) left = itemBox.left - containerBox.left;
      else if (itemBox.right > containerBox.right) left = itemBox.right - containerBox.right;

      if (itemBox.top < containerBox.top) top = itemBox.top - containerBox.top;
      else if (itemBox.bottom > containerBox.bottom) top = itemBox.bottom - containerBox.bottom;

      if (Math.abs(left) > 1 || Math.abs(top) > 1) {
        container.scrollBy({ left, top, behavior: 'auto' });
      }
    };

    const selectMedia = (index, options = {}) => {
      if (!mediaItems.length) return;
      const {
        announce = true,
        focusThumbnail = false,
        scrollMedia = true,
        scrollThumbnail = true
      } = options;
      activeMediaIndex = Math.min(Math.max(index, 0), mediaItems.length - 1);

      mediaItems.forEach((media, mediaIndex) => {
        if (mediaIndex === activeMediaIndex) {
          media.setAttribute('aria-current', 'true');
        } else {
          media.removeAttribute('aria-current');
        }
      });

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === activeMediaIndex;
        if (isActive) {
          thumbnail.setAttribute('aria-current', 'true');
        } else {
          thumbnail.removeAttribute('aria-current');
        }
        thumbnail.tabIndex = isActive ? 0 : -1;
      });

      if (scrollMedia) {
        gallery?.scrollTo({ left: mediaItems[activeMediaIndex].offsetLeft, behavior: 'auto' });
      }

      const activeThumbnail = thumbnails[activeMediaIndex];
      if (focusThumbnail && activeThumbnail) {
        activeThumbnail.focus({ preventScroll: true });
      }
      if (scrollThumbnail && activeThumbnail) {
        scrollItemWithin(thumbnailList, activeThumbnail);
      }

      if (announce && galleryStatus) {
        galleryStatus.textContent = `Image ${activeMediaIndex + 1} of ${mediaItems.length}.`;
      }
    };

    const updateLightbox = (index) => {
      if (!galleryDialog || !lightboxMedia || !mediaItems.length) return;

      selectMedia(index, {
        announce: false,
        scrollMedia: false,
        scrollThumbnail: false
      });
      const sourceImage = mediaItems[activeMediaIndex].querySelector('img');
      if (!sourceImage) return;

      const lightboxImage = sourceImage.cloneNode(true);
      lightboxImage.className = 'rv-product-lightbox__image';
      lightboxImage.loading = 'eager';
      lightboxImage.fetchPriority = 'high';
      lightboxImage.sizes = '100vw';
      lightboxMedia.replaceChildren(lightboxImage);

      const position = `Image ${activeMediaIndex + 1} of ${mediaItems.length}.`;
      if (lightboxStatus) lightboxStatus.textContent = position;
      if (lightboxPrevious) lightboxPrevious.disabled = activeMediaIndex === 0;
      if (lightboxNext) lightboxNext.disabled = activeMediaIndex === mediaItems.length - 1;
    };

    const openLightbox = (index) => {
      if (!galleryDialog || typeof galleryDialog.showModal !== 'function') return false;
      updateLightbox(index);
      if (!galleryDialog.open) galleryDialog.showModal();
      lightboxClose?.focus();
      return true;
    };

    gallery?.querySelectorAll('[data-rv-gallery-open]').forEach((opener, index) => {
      opener.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (openLightbox(index)) event.preventDefault();
      });
    });

    thumbnails.forEach((thumbnail, index) => {
      let pointerSelection = false;
      thumbnail.addEventListener('pointerdown', () => {
        pointerSelection = true;
      });
      thumbnail.addEventListener('focus', () => {
        if (pointerSelection) return;
        if (index !== activeMediaIndex) selectMedia(index, { scrollThumbnail: false });
      });
      thumbnail.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        selectMedia(index, { scrollThumbnail: false });
        pointerSelection = false;
      });
      const clearPointerSelection = () => {
        pointerSelection = false;
      };
      thumbnail.addEventListener('pointercancel', clearPointerSelection);
      thumbnail.addEventListener('pointerup', clearPointerSelection);
      thumbnail.addEventListener('blur', clearPointerSelection);
    });

    thumbnailList?.addEventListener('keydown', (event) => {
      const focusedThumbnail = event.target.closest('[data-rv-gallery-thumbnail]');
      const focusedIndex = thumbnails.indexOf(focusedThumbnail);
      let nextIndex = focusedIndex >= 0 ? focusedIndex : activeMediaIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex += 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex -= 1;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = thumbnails.length - 1;
      else return;

      event.preventDefault();
      selectMedia(nextIndex, { focusThumbnail: true });
    });

    gallery?.addEventListener(
      'scroll',
      () => {
        if (galleryScrollFrame) window.cancelAnimationFrame(galleryScrollFrame);
        galleryScrollFrame = window.requestAnimationFrame(() => {
          const galleryBox = gallery.getBoundingClientRect();
          const galleryCenter = galleryBox.left + galleryBox.width / 2;
          let nearestIndex = activeMediaIndex;
          let nearestDistance = Number.POSITIVE_INFINITY;

          mediaItems.forEach((media, index) => {
            const mediaBox = media.getBoundingClientRect();
            const mediaCenter = mediaBox.left + mediaBox.width / 2;
            const distance = Math.abs(mediaCenter - galleryCenter);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = index;
            }
          });

          if (nearestIndex !== activeMediaIndex) {
            selectMedia(nearestIndex, { scrollMedia: false });
          }
          galleryScrollFrame = 0;
        });
      },
      { passive: true }
    );

    gallery?.addEventListener('keydown', (event) => {
      let nextIndex = activeMediaIndex;
      if (event.key === 'ArrowRight') nextIndex += 1;
      else if (event.key === 'ArrowLeft') nextIndex -= 1;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = mediaItems.length - 1;
      else return;

      event.preventDefault();
      selectMedia(nextIndex);
    });

    lightboxPrevious?.addEventListener('click', () => updateLightbox(activeMediaIndex - 1));
    lightboxNext?.addEventListener('click', () => updateLightbox(activeMediaIndex + 1));
    lightboxClose?.addEventListener('click', () => galleryDialog?.close());

    galleryDialog?.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        const focusable = [
          ...galleryDialog.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ].filter((element) => !element.hidden && element.getClientRects().length > 0);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }

        const firstFocusable = focusable[0];
        const lastFocusable = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        } else if (!event.shiftKey && document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        } else if (!galleryDialog.contains(document.activeElement)) {
          event.preventDefault();
          firstFocusable.focus();
        }
        return;
      }

      let nextIndex = activeMediaIndex;
      if (event.key === 'ArrowRight') nextIndex += 1;
      else if (event.key === 'ArrowLeft') nextIndex -= 1;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = mediaItems.length - 1;
      else return;

      event.preventDefault();
      updateLightbox(nextIndex);
    });

    galleryDialog?.addEventListener('close', () => {
      selectMedia(activeMediaIndex, { announce: false });
      window.requestAnimationFrame(() => {
        mediaItems[activeMediaIndex]
          ?.querySelector('[data-rv-gallery-open]')
          ?.focus({ preventScroll: true });
      });
    });

    const productForm = product.querySelector('[data-rv-product-form]');
    const submitButton = productForm?.querySelector('[data-rv-add-to-cart]');
    const cartStatus = productForm?.querySelector('[data-rv-cart-status]');
    const shareButton = product.querySelector('[data-rv-product-share]');
    const shareStatus = product.querySelector('[data-rv-share-status]');
    let submitResetTimer = 0;

    if (shareButton) {
      const defaultLabel = shareButton.dataset.rvShareLabel || 'SHARE THIS PIECE';
      const successLabel = shareButton.dataset.rvShareSuccess || 'LINK COPIED';
      const textLabel = shareButton.querySelector('span');
      let shareResetTimer = 0;

      const confirmShare = (message) => {
        if (shareResetTimer) window.clearTimeout(shareResetTimer);
        if (textLabel) textLabel.textContent = successLabel;
        if (shareStatus) shareStatus.textContent = message;
        shareResetTimer = window.setTimeout(() => {
          if (textLabel) textLabel.textContent = defaultLabel;
          shareResetTimer = 0;
        }, 2200);
      };

      const copyShareUrl = async (url) => {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          return;
        }

        const input = document.createElement('textarea');
        input.value = url;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.append(input);
        input.select();
        const copied = document.execCommand('copy');
        input.remove();
        if (!copied) throw new Error('Copy failed');
      };

      shareButton.addEventListener('click', async () => {
        const title = shareButton.dataset.rvShareTitle || document.title;
        const url = shareButton.dataset.rvShareUrl || window.location.href;

        try {
          if (navigator.share) {
            try {
              await navigator.share({ title, url });
              if (shareStatus) shareStatus.textContent = 'Share options opened.';
              return;
            } catch (error) {
              if (error?.name === 'AbortError') return;
            }
          }

          await copyShareUrl(url);
          confirmShare('Product link copied.');
        } catch (error) {
          if (shareStatus) shareStatus.textContent = 'The link could not be copied. Use your browser address bar instead.';
        }
      });
    }

    const updateCartCount = (cart) => {
      if (!cart || typeof cart.item_count !== 'number') return;
      document.querySelectorAll('[data-rv-cart-open]').forEach((cartLink) => {
        let count = cartLink.querySelector('.rv-cart-count');
        if (!count && cart.item_count > 0) {
          count = document.createElement('span');
          count.className = 'rv-cart-count';
          count.setAttribute('aria-hidden', 'true');
          cartLink.append(count);
        }
        if (count) {
          count.textContent = String(cart.item_count);
          count.hidden = cart.item_count === 0;
        }
        cartLink.setAttribute(
          'aria-label',
          `Bag, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`
        );
      });
    };

    const showAddSuccess = (message = 'Added to your bag.') => {
      submitButton.textContent = submitButton.dataset.rvLabelSuccess || 'Added to Bag';
      if (!cartStatus) return;
      cartStatus.textContent = message;
      cartStatus.dataset.state = 'success';
      cartStatus.setAttribute('role', 'status');
      cartStatus.hidden = false;
    };

    const showAddError = (message = 'This piece could not be added. Please try again.') => {
      submitButton.textContent = submitButton.dataset.rvLabelDefault || 'Add to Bag';
      if (!cartStatus) return;
      cartStatus.textContent = message;
      cartStatus.dataset.state = 'error';
      cartStatus.setAttribute('role', 'alert');
      cartStatus.hidden = false;
    };

    const showAddReview = (message) => {
      submitButton.textContent = submitButton.dataset.rvLabelDefault || 'Add to Bag';
      if (!cartStatus) return;
      cartStatus.textContent = message;
      cartStatus.dataset.state = 'review';
      cartStatus.setAttribute('role', 'alert');
      cartStatus.hidden = false;
    };

    if (productForm && submitButton && window.fetch && window.FormData) {
      productForm.addEventListener('submit', async (event) => {
        if (productForm.dataset.rvSubmitting === 'true') {
          event.preventDefault();
          return;
        }
        if (!productForm.checkValidity()) return;

        event.preventDefault();
        if (submitResetTimer) window.clearTimeout(submitResetTimer);
        productForm.dataset.rvSubmitting = 'true';
        submitButton.disabled = true;
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.textContent = submitButton.dataset.rvLabelLoading || 'Adding…';
        if (cartStatus) {
          cartStatus.hidden = true;
          cartStatus.textContent = '';
          cartStatus.dataset.state = '';
          cartStatus.setAttribute('role', 'status');
        }

        const root = window.Shopify?.routes?.root || '/';
        const cartDrawer = window.RonniesCartDrawer;
        const drawerRequest = cartDrawer?.getRequestData?.() || null;
        const previousCount = drawerRequest ? cartDrawer.getCount() : null;
        const drawerMutation = drawerRequest ? cartDrawer.beginMutation?.() || null : null;
        let responseReceived = false;
        let responseWasOk = null;

        try {
          if (drawerRequest && !drawerMutation) {
            showAddReview(
              cartDrawer.getMessage('updating') || 'Your bag is updating. Review it before adding again.'
            );
            return;
          }

          const formData = new FormData(productForm);
          if (drawerRequest) {
            formData.set('sections', drawerRequest.sectionId);
            formData.set('sections_url', drawerRequest.sectionsUrl);
          }

          const response = await window.fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });
          responseReceived = true;
          responseWasOk = response.ok;
          const result = await response.json().catch(() => ({}));
          let drawerState = null;
          if (drawerRequest) {
            drawerState = await cartDrawer.reconcileMutation(drawerMutation, {
              sectionHTML: result?.sections?.[drawerRequest.sectionId],
              responseOk: response.ok,
              previousCount,
              open: response.ok,
              opener: submitButton
            });
          }

          if (!response.ok) {
            const confirmedNotApplied = drawerState?.hydrated === true
              && Number.isInteger(previousCount)
              && drawerState.countIncreased === false;
            if (confirmedNotApplied || !drawerRequest) throw new Error('Cart add failed');

            if (!drawerState?.stale && drawerState?.countIncreased !== true) {
              cartDrawer.open(submitButton);
            }
            showAddReview(
              drawerState?.hydrated
                ? cartDrawer.getMessage('review')
                  || 'Your bag was refreshed. Review the current quantity.'
                : cartDrawer.getMessage('refreshFailed')
                  || 'We could not confirm the latest bag. View the full bag before checkout.'
            );
            return;
          }

          showAddSuccess('Added to your bag.');

          if (!drawerRequest) try {
            const cartResponse = await window.fetch(`${root}cart.js`, {
              headers: { Accept: 'application/json' }
            });
            if (cartResponse.ok) {
              const cart = await cartResponse.json();
              updateCartCount(cart);
              document.dispatchEvent(new CustomEvent('rv:cart:updated', { detail: { cart } }));
            }
          } catch (_cartError) {
            // The item was added; a stale visual count must not turn success into an error.
          }
        } catch (_error) {
          if (responseReceived && responseWasOk === false) {
            showAddError('This item could not be added. Check the details and try again.');
          } else if (drawerRequest) {
            const drawerState = await cartDrawer.reconcileMutation(drawerMutation, {
              responseOk: false,
              previousCount,
              open: true,
              opener: submitButton
            });
            showAddReview(
              drawerState.hydrated
                ? cartDrawer.getMessage('review')
                  || 'Your bag was refreshed. Review the current quantity.'
                : cartDrawer.getMessage('refreshFailed')
                  || 'We could not confirm the latest bag. View the full bag before checkout.'
            );
          } else {
            showAddReview("We couldn't confirm your bag. Open your bag before trying again.");
          }
        } finally {
          if (drawerMutation) cartDrawer.endMutation(drawerMutation);
          productForm.dataset.rvSubmitting = 'false';
          submitButton.removeAttribute('aria-busy');
          const selectedOption = productForm.querySelector('[data-rv-variant-select] option:checked');
          const isAvailable = !selectedOption || selectedOption.dataset.available === 'true';
          submitButton.disabled = !isAvailable;
          submitButton.setAttribute('aria-disabled', String(!isAvailable));

          if (submitButton.textContent === (submitButton.dataset.rvLabelSuccess || 'Added to Bag')) {
            submitResetTimer = window.setTimeout(() => {
              submitButton.textContent = submitButton.dataset.rvLabelDefault || 'Add to Bag';
              submitResetTimer = 0;
            }, 1800);
          }
        }
      });
    }

    const variantSelect = product.querySelector('[data-rv-variant-select]');
    if (variantSelect) {
      const price = product.querySelector('[data-rv-price]');
      const currentPrice = price?.querySelector('[data-rv-price-current]');
      const comparePrice = price?.querySelector('[data-rv-price-compare]');
      const variantStatus = product.querySelector('[data-rv-variant-status]');

      const updateVariant = (updateUrl = true) => {
        const option = variantSelect.options[variantSelect.selectedIndex];
        if (!option) return;

        const isAvailable = option.dataset.available === 'true';
        const compareAtPrice = option.dataset.compareAtPrice || '';

        if (currentPrice) currentPrice.textContent = option.dataset.price || '';
        if (comparePrice) {
          comparePrice.textContent = compareAtPrice;
          comparePrice.hidden = !compareAtPrice;
        }
        price?.classList.toggle('rv-price--sale', Boolean(compareAtPrice));

        if (submitButton) {
          const isSubmitting = productForm?.dataset.rvSubmitting === 'true';
          submitButton.disabled = !isAvailable || isSubmitting;
          submitButton.setAttribute('aria-disabled', String(!isAvailable || isSubmitting));
        }

        if (variantStatus) {
          const availability = isAvailable ? 'Available.' : 'Unavailable.';
          variantStatus.textContent = `${option.dataset.title || 'Selected option'}. ${availability}`;
        }

        if (updateUrl && option.dataset.url) {
          window.history.replaceState({}, '', option.dataset.url);
        }

        if (option.dataset.mediaId) {
          const mediaIndex = mediaItems.findIndex(
            (media) => media.dataset.mediaId === option.dataset.mediaId
          );
          if (mediaIndex >= 0) selectMedia(mediaIndex);
        }
      };

      variantSelect.addEventListener('change', () => updateVariant());
      updateVariant(false);
    }

    selectMedia(activeMediaIndex, {
      announce: false,
      scrollMedia: false,
      scrollThumbnail: false
    });

    product.dataset.rvReady = 'true';
  }

  function initialise(scope = document) {
    scope.querySelectorAll('[data-rv-product]').forEach(initialiseProduct);
  }

  initialise();

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
